// ─────────────────────────────────────────────────────────────────────────────
// SectionHeading.jsx 
//
// Usage:
//   <SectionHeading
//     label="Our Solutions"
//     title="Science-Driven Products for Every Sector"
//     subtitle="Premium feed additives..."
//   />
//
//   <SectionHeading
//     label="About Us"
//     title="Who We Are"
//     align="center"
//     theme="dark"       // 'light' (default) | 'dark' (white text on green bg)
//   />
//
//   <SectionHeading
//     label="News"
//     title="Salient News"
//     linkLabel="See all posts"
//     linkTo="/blog"     // adds a right-side link (desktop: flex row)
//   />
// ─────────────────────────────────────────────────────────────────────────────

import { Link } from 'react-router-dom'

export default function SectionHeading({
  label,         // small uppercase tag above title (optional)
  title,         // main heading (required)
  subtitle,      // paragraph below title (optional)
  align    = 'left',      // 'left' | 'center'
  theme    = 'light',     // 'light' | 'dark'
  linkLabel,     // optional right-side link label e.g. "See all posts"
  linkTo,        // internal link for right-side link
  linkHref,      // external link for right-side link
  style: extraStyle = {},
}) {
  const isDark   = theme === 'dark'
  const isCenter = align === 'center'

  const colors = {
    label:    isDark ? 'rgba(255,255,255,0.60)' : 'rgba(0,0,0,0.45)',
    title:    isDark ? '#ffffff'                : '#007935',
    subtitle: isDark ? 'rgba(255,255,255,0.80)' : '#55544e',
    link:     isDark ? 'rgba(255,255,255,0.75)' : '#007935',
  }

  const rightLink = (linkLabel && (linkTo || linkHref)) ? (
    linkTo ? (
      <Link
        to={linkTo}
        style={{
          fontFamily:     "'DM Sans', sans-serif",
          fontSize:       '13px', fontWeight: 600,
          color:          colors.link, textDecoration: 'none',
          display:        'inline-flex', alignItems: 'center', gap: '5px',
          paddingBottom:  '2px',
          borderBottom:   `1px solid ${colors.link}`,
          flexShrink:     0, transition: 'opacity 0.2s ease',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      >
        {linkLabel}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 6h8M6 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </Link>
    ) : (
      <a
        href={linkHref} target="_blank" rel="noopener noreferrer"
        style={{
          fontFamily:     "'DM Sans', sans-serif",
          fontSize:       '13px', fontWeight: 600,
          color:          colors.link, textDecoration: 'none',
          display:        'inline-flex', alignItems: 'center', gap: '5px',
          paddingBottom:  '2px',
          borderBottom:   `1px solid ${colors.link}`,
          flexShrink:     0,
        }}
      >
        {linkLabel}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 6h8M6 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </a>
    )
  ) : null

  return (
    <div style={{
      display:        rightLink ? 'flex' : 'block',
      alignItems:     rightLink ? 'flex-end' : undefined,
      justifyContent: rightLink ? 'space-between' : undefined,
      gap:            rightLink ? '24px' : undefined,
      flexWrap:       rightLink ? 'wrap' : undefined,
      textAlign:      isCenter ? 'center' : 'left',
      ...extraStyle,
    }}>

      {/* ── Left / main content ── */}
      <div style={{ ...(isCenter ? { margin: '0 auto' } : {}) }}>

        {/* Label — small uppercase muted text */}
        {label && (
          <p style={{
            fontFamily:    "'DM Sans', sans-serif",
            fontSize:      '13px', fontWeight: 400,
            letterSpacing: '0.13em', textTransform: 'uppercase',
            color:         colors.label,
            margin:        '0 0 8px',
          }}>
            {label}
          </p>
        )}

        {/* Title — DM Sans uppercase like MIAVIT sections */}
        {title && (
          <h2 style={{
            fontFamily:    "'DM Sans', sans-serif",
            fontSize:      'clamp(1.5rem, 2.5vw, 2rem)',
            fontWeight:    400,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color:         colors.title,
            lineHeight:    1.2,
            margin:        subtitle ? '0 0 12px' : '0',
          }}>
            {title}
          </h2>
        )}

        {/* Subtitle */}
        {subtitle && (
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize:   '14px', fontWeight: 400,
            color:      colors.subtitle, lineHeight: 1.7,
            margin:     0,
            maxWidth:   isCenter ? '560px' : '520px',
          }}>
            {subtitle}
          </p>
        )}
      </div>

      {/* ── Right link (if provided) ── */}
      {rightLink}
    </div>
  )
}